import {
  type ReactNode,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { message, Modal, Timeline } from "antd";
import type { TimelineItemProps } from "antd";
import {
  AppstoreOutlined,
  ArrowLeftOutlined,
  AuditOutlined,
  CalendarOutlined,
  CheckCircleOutlined,
  FileTextOutlined,
  GlobalOutlined,
  IdcardOutlined,
  NodeIndexOutlined,
  PlusSquareOutlined,
  ThunderboltOutlined,
  FilePdfOutlined,
  LockOutlined,
} from "@ant-design/icons";
import type { Language, ScanResponse } from "../../types";
import {
  fullDocumentRewriteFromClaims,
  localizeScanForDisplay,
} from "../../services/compliance";
import type { textMap } from "../../i18n";
import type { StudioSeriesTaskDto } from "../../services/compliance";
import { fetchStudioSeriesTasks } from "../../services/compliance";
import { copyTextToClipboard } from "../../utils/clipboard";
import { getLocalSeriesTimeline } from "./studioSeries";
import { formatUiDateTimeMed } from "../../utils/uiDateFormat";
import { downloadJson } from "../../utils/exportFile";
import { markScanFinalized } from "./studioScanHistoryStorage";
import { ExportPdf } from "../../components/ExportPdf";
import "../../styles/studio-result-detail.css";

const AVATAR_URL =
  "https://lh3.googleusercontent.com/aida-public/AB6AXuBS3U04kcpY_Nd7FeOkHadz8AX2mZHtQu8WkjtrojFlKpEKcPVolKQKpI42FP1_WXSUV7cE7dHhnvwwWDMARuNLioo0f9lt3tljW41OTL6-tXeDruhNWMYcHOTPZKS2S8OgPKbHGHySutxxJB6FwagMrqzchjDiMILkRNH6MZYbTuftbnfwJpRF3sj2ncApBQecnSrmcA08DA9F_dBZ_VPaVHp5p6klMYAJx_Ck_zUk-0wgqzmCQ87AITZMe_RYjCbgR5V15UFMFcw0";

const CIRC = 2 * Math.PI * 40;

function fdaSourceHref(ev: { url?: string; source?: string }): string | null {
  const u = ev.url?.trim();
  if (u && /^https?:\/\//i.test(u)) {
    return u;
  }
  const s = ev.source?.trim();
  if (s && /^https?:\/\//i.test(s)) {
    return s;
  }
  return null;
}

type T = (typeof textMap)["en"];

type Props = {
  result: ScanResponse;
  lang: Language;
  t: T;
  isDark: boolean;
  productTitle: string;
  verticalLabel: string;
  skuLabel: string;
  scannedAt: Date;
  scanIdShort: string;
  onToggleLang: () => void;
  onToggleTheme: () => void;
  onRerunScan: () => void | Promise<void>;
  onDiscard: () => void;
  onBackToWorkspace: () => void;
  onBack: () => void;
  backLabel: string;
  /** 工作台内嵌：不渲染独立顶栏，仅主内容区 + 顶栏「返回」 */
  embedded?: boolean;
  /** 将 scan 写入对比集并跳转记录列表（由详情页注入） */
  onAddToCompare?: () => void;
  /** 后端 review_tasks.id，用于系列时间线与接口对齐 */
  backendTaskId?: string;
  /** 关联系列 id（review_series.id） */
  seriesId?: string;
  /** 发起扫描时的原文案；用于全文改写展示兜底 */
  claimsText: string;
  /** 本机已定稿时间（毫秒），来自 localStorage */
  finalizedAt?: number;
  /** 定稿写入 localStorage 后回调，用于父级刷新 record */
  onFinalizeComplete?: () => void;
  /** 内嵌工作台：顶栏右侧展示剩余审查额度（与 Chat 一致） */
  creditsPill?: ReactNode;
};

export function StudioScanResultDetail({
  result,
  lang,
  t,
  isDark,
  productTitle,
  verticalLabel,
  skuLabel,
  scannedAt,
  scanIdShort,
  onToggleLang,
  onToggleTheme,
  onRerunScan,
  onDiscard,
  onBackToWorkspace,
  onBack,
  backLabel,
  embedded = false,
  onAddToCompare,
  backendTaskId,
  seriesId,
  claimsText,
  finalizedAt,
  onFinalizeComplete,
  creditsPill,
}: Props) {
  const [timelineOpen, setTimelineOpen] = useState(false);
  const [serverTimeline, setServerTimeline] = useState<
    StudioSeriesTaskDto[] | null
  >(null);
  const [isExiting, setIsExiting] = useState(false);
  const [exportPdfOpen, setExportPdfOpen] = useState(false);
  const exitTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const displayResult = useMemo(
    () => localizeScanForDisplay(result, lang),
    [result, lang],
  );

  useEffect(() => {
    return () => {
      if (exitTimerRef.current) clearTimeout(exitTimerRef.current);
    };
  }, []);

  useEffect(() => {
    if (!timelineOpen || !seriesId) {
      setServerTimeline(null);
      return;
    }
    let cancelled = false;
    void fetchStudioSeriesTasks(seriesId)
      .then((rows) => {
        if (!cancelled) {
          setServerTimeline(rows);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setServerTimeline([]);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [timelineOpen, seriesId]);

  const handleBack = () => {
    if (isExiting) return;
    setIsExiting(true);
    exitTimerRef.current = setTimeout(() => {
      exitTimerRef.current = null;
      onBack();
    }, 240);
  };

  const score = Math.max(0, Math.min(100, result.score));
  const dashOffset = CIRC * (1 - score / 100);

  const riskLevel = useMemo(() => {
    if (score < 55) return "high" as const;
    if (score < 75) return "medium" as const;
    return "low" as const;
  }, [score]);

  const riskLabel =
    riskLevel === "high"
      ? lang === "zh"
        ? "高风险"
        : "High Risk"
      : riskLevel === "medium"
        ? lang === "zh"
          ? "中风险"
          : "Medium Risk"
        : lang === "zh"
          ? "低风险"
          : "Low Risk";

  const dateStr = formatUiDateTimeMed(scannedAt, lang);

  const strokeColor =
    riskLevel === "high"
      ? "#ba1a1a"
      : riskLevel === "medium"
        ? "#d97706"
        : "#15803d";

  const criticalCount = result.flaggedItems.filter(
    (x) => x.risk === "high" || x.risk === "medium",
  ).length;

  const localTimeline = useMemo(
    () => getLocalSeriesTimeline(scanIdShort),
    [scanIdShort],
  );

  const useServerTimeline =
    Boolean(seriesId) &&
    serverTimeline !== null &&
    serverTimeline.length > 0;

  const timelineItems = useMemo((): TimelineItemProps[] => {
    const tagCurrent = lang === "zh" ? "当前扫描" : "Current scan";
    const tagSeries = lang === "zh" ? "系列内版本" : "Series version";

    if (useServerTimeline && serverTimeline?.length) {
      return [...serverTimeline]
        .sort(
          (a, b) =>
            new Date(a.created_at).getTime() -
            new Date(b.created_at).getTime(),
        )
        .map((r) => {
          const sc =
            r.score != null ? `${Math.max(0, Math.min(100, r.score))}` : "—";
          const when = formatUiDateTimeMed(r.created_at, lang);
          const isCurrent =
            Boolean(backendTaskId) && r.task_id === backendTaskId;
          const ver =
            r.version_no != null
              ? `${lang === "zh" ? "版本" : "v"}${r.version_no}`
              : null;
          return {
            color: isCurrent ? "green" : "blue",
            dot: isCurrent ? <CheckCircleOutlined /> : undefined,
            children: (
              <div>
                <div style={{ fontWeight: 700 }}>{when}</div>
                <div style={{ fontSize: 13, marginTop: 4, opacity: 0.85 }}>
                  {isCurrent ? tagCurrent : tagSeries}
                  {ver ? ` · ${ver}` : ""}
                  {" · "}
                  {lang === "zh" ? "任务 ID" : "Task ID"}: {r.task_id}
                </div>
                <div style={{ fontSize: 13, marginTop: 2 }}>
                  {sc}/100
                  {r.product_display_name
                    ? ` · ${r.product_display_name}`
                    : ""}
                </div>
              </div>
            ),
          };
        });
    }

    return localTimeline.map((r) => {
      const sc = Math.max(0, Math.min(100, r.result.score));
      const when = formatUiDateTimeMed(r.completedAt, lang);
      const isCurrent = r.scanDisplayId === scanIdShort;
      return {
        color: isCurrent ? "green" : "blue",
        dot: isCurrent ? <CheckCircleOutlined /> : undefined,
        children: (
          <div>
            <div style={{ fontWeight: 700 }}>{when}</div>
            <div style={{ fontSize: 13, marginTop: 4, opacity: 0.85 }}>
              {isCurrent ? tagCurrent : tagSeries}
              {" · "}
              {lang === "zh" ? "扫描 ID" : "Scan ID"}: {r.scanDisplayId}
            </div>
            <div style={{ fontSize: 13, marginTop: 2 }}>{sc}/100</div>
          </div>
        ),
      };
    });
  }, [
    useServerTimeline,
    serverTimeline,
    localTimeline,
    lang,
    backendTaskId,
    scanIdShort,
  ]);

  const copy = {
    scanId: lang === "zh" ? "扫描 ID" : "Scan ID",
    version: lang === "zh" ? "V1 • 进行中" : "V1 • Active",
    complianceScore: lang === "zh" ? "合规分" : "Compliance Score",
    findingsTitle: lang === "zh" ? "发现" : "Findings",
    issuesSuffix:
      lang === "zh"
        ? `（${criticalCount} 项需关注）`
        : `(${criticalCount} critical issues detected)`,
    viewAll: lang === "zh" ? "查看全部" : "View All",
    flagged: lang === "zh" ? "标记表述" : "Flagged phrase",
    risk: lang === "zh" ? "风险" : "Risk",
    reason: t.reason,
    rewrite: t.suggestion,
    evidenceTitle: lang === "zh" ? "证据与来源" : "Evidence & sources",
    fdaBlockTitle:
      lang === "zh" ? "公开监管证据（FDA / FTC）" : "Regulatory evidence (FDA / FTC)",
    fdaBlockLead:
      lang === "zh"
        ? "引用 FDA 公开执法数据（openFDA 同步）及 FTC 消费者保护等相关新闻稿（官方 RSS）作证据提示，持续扩充数据源。以下为本次扫描命中的相关摘要。"
        : "We cite FDA public enforcement (openFDA sync) and FTC consumer-protection press releases (official RSS) as contextual evidence signals; we continuously expand data sources. Below are matched snippets for this scan.",
    fdaCountLabel: (n: number) =>
      lang === "zh" ? `共 ${n} 条命中` : `${n} match${n === 1 ? "" : "es"}`,
    fdaMetaTerm: lang === "zh" ? "关联词或主题" : "Term or topic",
    fdaMetaProduct: lang === "zh" ? "涉及产品（若有）" : "Product (if any)",
    fdaMetaClass: lang === "zh" ? "分类" : "Classification",
    fdaMetaDate: lang === "zh" ? "报告日期" : "Report date",
    fdaMetaSource: lang === "zh" ? "来源说明" : "Source",
    fdaViewSource: lang === "zh" ? "打开来源链接" : "Open source link",
    fdaEndpointOnline: lang === "zh" ? "监管数据在线" : "Regulatory data online",
    fdaEndpointOffline: lang === "zh" ? "监管数据离线" : "Regulatory data offline",
    insight: lang === "zh" ? "系统洞察" : "System Insight",
    applyDisclaimer:
      lang === "zh" ? "应用自动免责声明" : "Apply Auto-Disclaimers",
    addCompare: seriesId
      ? lang === "zh"
        ? "版本对比"
        : "Version compare"
      : lang === "zh"
        ? "版本对比（需系列）"
        : "Compare (needs series)",
    fullRewriteTitle:
      lang === "zh" ? "一键改写（全文）" : "Full rewrite (one-click)",
    fullRewriteHint:
      lang === "zh"
        ? "请使用「复制全文」，再粘贴到您的产品或营销文案编辑器；当前页不会自动替换扫描输入框内的原文。"
        : "Use “Copy full text”, then paste into your product or marketing copy editor. The scan form is not auto-replaced on this page.",
    fullRewriteCopy: lang === "zh" ? "复制全文" : "Copy full text",
    fullRewriteCopiedOk:
      lang === "zh" ? "已复制全文改写" : "Full rewrite copied",
    rescanVersion:
      lang === "zh" ? "另存为新版本再扫" : "Re-scan as New Version",
    timeline: lang === "zh" ? "打开系列时间线" : "Open Series Timeline",
    discard: lang === "zh" ? "放弃结果" : "Discard Result",
    pro: lang === "zh" ? "Pro 账户" : "Pro Account",
    user: lang === "zh" ? "用户" : "User",
    theme: lang === "zh" ? "主题" : "Theme",
    taskId: lang === "zh" ? "任务 ID" : "Task ID",
  };

  const displayFullRewrite = useMemo(() => {
    const fromApi = result.full_rewrite_text?.trim();
    if (fromApi) return fromApi;
    return fullDocumentRewriteFromClaims(claimsText, lang).trim();
  }, [result.full_rewrite_text, claimsText, lang]);

  const fdaEvidence = result.fda?.evidence ?? [];
  const fdaEp = (result.fda?.endpoint_status ?? "").toLowerCase();
  const fdaEpPositive = fdaEp === "online" || fdaEp === "ok";

  const insightText =
    lang === "zh"
      ? `当前合规分为 ${score}。建议弱化绝对化与疾病治疗暗示，并在页脚补充结构/功能说明及「不用于诊断、治疗」类免责声明以降低监管关注。`
      : `Compliance score is ${score}. Consider softening absolute and disease claims, and add a structure/function disclaimer in the footer to reduce FDA scrutiny.`;

  const copyFullRewrite = async () => {
    const text = displayFullRewrite;
    if (!text) return;
    const ok = await copyTextToClipboard(text);
    if (ok) {
      message.success(copy.fullRewriteCopiedOk);
    } else {
      message.error(t.studioDetailDisclaimerCopyFailed);
    }
  };

  const copyDisclaimerDraft = async () => {
    const block =
      lang === "zh"
        ? `【营销页脚建议文案】\n本产品相关陈述未经 FDA 评估，不用于诊断、治疗或预防任何疾病。如您有健康问题请咨询专业医师。\n\n【与本次扫描相关的系统建议】\n${insightText}`
        : `[Suggested footnote language]\nThese statements have not been evaluated by the FDA. This product is not intended to diagnose, treat, cure, or prevent any disease.\n\n[System guidance for this scan]\n${insightText}`;
    const ok = await copyTextToClipboard(block);
    if (ok) {
      message.success(t.studioDetailDisclaimerCopied);
    } else {
      message.error(t.studioDetailDisclaimerCopyFailed);
    }
  };

  const handleFinalizeClick = () => {
    Modal.confirm({
      title: t.studioDetailFinalizeConfirmTitle,
      content: t.studioDetailFinalizeConfirmBody,
      okText: t.studioDetailFinalizeOk,
      cancelText: t.studioDetailFinalizeCancel,
      onOk: () => {
        const at = Date.now();
        const stamp = new Date(at).toISOString().replace(/:/g, "-").slice(0, 19);
        const report = {
          schema: "xtool_studio_finalize_report_v1",
          finalized_at_ms: at,
          finalized_at_iso: new Date(at).toISOString(),
          locale: lang,
          scan_display_id: scanIdShort,
          backend_task_id: backendTaskId ?? null,
          series_id: seriesId ?? null,
          product_display_name: productTitle,
          vertical_label: verticalLabel,
          scanned_at_ms: scannedAt.getTime(),
          claims_text: claimsText,
          result,
        };
        downloadJson(`xtool-final-report-${scanIdShort}-${stamp}.json`, report);
        const ok = markScanFinalized(scanIdShort, at);
        if (ok) {
          message.success(t.studioDetailFinalizeSuccess);
          onFinalizeComplete?.();
        } else {
          message.error(t.studioDetailFinalizeFailed);
        }
      },
    });
  };

  const backButton = (
    <button
      type="button"
      className="studio-detail-back-btn"
      onClick={handleBack}
      disabled={isExiting}
      aria-busy={isExiting}
    >
      <span className="studio-detail-back-icon" aria-hidden>
        <ArrowLeftOutlined />
      </span>
      {backLabel}
    </button>
  );

  return (
    <div
      className={`studio-detail-root${embedded ? " studio-detail-root--embedded" : ""} ${isDark ? "studio-detail-dark" : ""}${isExiting ? " studio-detail-root--exit" : ""}`}
    >
      {!embedded ? (
        <header className="studio-detail-topbar">
          <div className="studio-detail-topbar-inner">
            <div className="studio-detail-topbar-left">
              {backButton}
              <div className="studio-detail-topbar-brand">
                
                <span className="studio-detail-logo font-headline">
                  XTool Studio
                </span>
                <span className="studio-detail-topbar-muted">/</span>
                <span className="studio-detail-topbar-crumb">
                  {t.studioCrumbDetail}
                </span>
              </div>
            </div>
            <div className="studio-detail-topbar-right">
              <button
                type="button"
                className="studio-detail-topbar-workspace"
                onClick={onBackToWorkspace}
              >
                {t.studioBackToWorkspace}
              </button>
              <button
                type="button"
                className="studio-detail-lang-btn"
                onClick={onToggleLang}
              >
                <GlobalOutlined />
                EN / 中文
              </button>
              <button
                type="button"
                className="studio-detail-lang-btn"
                onClick={onToggleTheme}
                aria-label={copy.theme}
              >
                {copy.theme}
              </button>
              <div className="studio-detail-user">
                <div className="studio-detail-user-text">
                  <div className="studio-detail-user-name">{copy.user}</div>
                  <div className="studio-detail-user-role">{copy.pro}</div>
                </div>
                <img className="studio-detail-avatar" src={AVATAR_URL} alt="" />
              </div>
            </div>
          </div>
        </header>
      ) : null}

      <main className="studio-detail-main">
        <div className="studio-detail-main-inner">
          {embedded ? (
            <div className="studio-detail-embed-toolbar">
              {backButton}
              {creditsPill ? (
                <div className="studio-detail-embed-credits">{creditsPill}</div>
              ) : null}
            </div>
          ) : null}
          <div className="studio-detail-summary-grid">
            <div className="studio-detail-score-col">
              <div className="studio-detail-gauge-wrap">
                <svg viewBox="0 0 96 96" aria-hidden>
                  <circle
                    cx="48"
                    cy="48"
                    r="40"
                    fill="transparent"
                    stroke="currentColor"
                    strokeWidth="8"
                    className={`studio-detail-gauge-track studio-detail-gauge-track--${riskLevel}`}
                  />
                  <circle
                    cx="48"
                    cy="48"
                    r="40"
                    fill="transparent"
                    stroke={strokeColor}
                    strokeWidth="8"
                    strokeLinecap="round"
                    strokeDasharray={CIRC}
                    strokeDashoffset={dashOffset}
                  />
                </svg>
                <span className="studio-detail-gauge-score">{score}/100</span>
              </div>
              <p className="studio-detail-score-label">
                {copy.complianceScore}
              </p>
            </div>
            <div className="studio-detail-product-block">
              <div className="studio-detail-product-title-row">
                <h1 className="studio-detail-product-title font-headline">
                  {productTitle}
                </h1>
                <span className={`studio-detail-risk-badge ${riskLevel}`}>
                  {riskLabel}
                </span>
              </div>
              <div className="studio-detail-meta-row">
                <span className="studio-detail-meta-item">
                  <AppstoreOutlined />
                  {verticalLabel}
                </span>
                <span className="studio-detail-meta-item">
                  <CalendarOutlined />
                  {dateStr}
                </span>
                <span className="studio-detail-meta-item">
                  <IdcardOutlined />
                  SKU: {skuLabel || "—"}
                </span>
                <span className="studio-detail-meta-item">
                  <FileTextOutlined />
                  {copy.scanId}: {scanIdShort}
                </span>
              </div>
            </div>
            <div className="studio-detail-export-wrap">
              <button
                type="button"
                className="studio-detail-export-btn"
                onClick={() => setExportPdfOpen(true)}
              >
                <FilePdfOutlined />
                {t.studioDetailExportPdf}
              </button>
            </div>
          </div>

          <div className="studio-detail-body-grid">
            <div>
              {displayFullRewrite ? (
                <section
                  className="studio-detail-full-rewrite"
                  aria-label={copy.fullRewriteTitle}
                >
                  <div className="studio-detail-full-rewrite-head">
                    <h2 className="studio-detail-full-rewrite-title font-headline">
                      {copy.fullRewriteTitle}
                    </h2>
                    <button
                      type="button"
                      className="studio-detail-full-rewrite-copy"
                      onClick={() => void copyFullRewrite()}
                    >
                      {copy.fullRewriteCopy}
                    </button>
                  </div>
                  <p className="studio-detail-full-rewrite-hint">{copy.fullRewriteHint}</p>
                  <pre className="studio-detail-full-rewrite-body">{displayFullRewrite}</pre>
                </section>
              ) : null}
              <div className="studio-detail-findings-head">
                <h2 className="studio-detail-findings-title font-headline">
                  {copy.findingsTitle}
                  <span className="studio-detail-findings-sub">
                    {copy.issuesSuffix}
                  </span>
                </h2>
                <button type="button" className="studio-detail-view-all">
                  {copy.viewAll}
                </button>
              </div>
              <div className="studio-detail-table-wrap">
                <table className="studio-detail-table">
                  <thead>
                    <tr>
                      <th>{copy.flagged}</th>
                      <th style={{ width: 96, textAlign: "center" }}>
                        {copy.risk}
                      </th>
                      <th>{copy.reason}</th>
                      <th>{copy.rewrite}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {displayResult.flaggedItems.length === 0 ? (
                      <tr>
                        <td colSpan={4} className="studio-detail-table-empty">
                          {t.studioPdfNoFlagged}
                        </td>
                      </tr>
                    ) : (
                      displayResult.flaggedItems.map((item, idx) => {
                        const phraseClass =
                          item.risk === "high"
                            ? "studio-detail-td-phrase-high"
                            : item.risk === "medium"
                              ? "studio-detail-td-phrase-med"
                              : "studio-detail-td-phrase-low";
                        return (
                          <tr key={idx}>
                            <td className={phraseClass}>
                              &quot;{item.phrase}&quot;
                            </td>
                            <td style={{ textAlign: "center" }}>
                              <span
                                className={`studio-detail-dot ${item.risk}`}
                              />
                            </td>
                            <td className="studio-detail-td-reason">
                              {item.reason}
                            </td>
                            <td className="studio-detail-td-rewrite">
                              {item.suggestion ? (
                                <span className="studio-detail-rewrite-body">
                                  &quot;{item.suggestion}&quot;
                                </span>
                              ) : (
                                "—"
                              )}
                            </td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            <div>
              <h2 className="studio-detail-evidence-title font-headline">
                {copy.evidenceTitle}
              </h2>
              <section
                className="studio-detail-fda-block"
                aria-label={copy.fdaBlockTitle}
              >
                <div className="studio-detail-fda-block-top">
                  <div className="studio-detail-fda-block-head">
                    <AuditOutlined
                      className="studio-detail-fda-block-icon"
                      aria-hidden
                    />
                    <div className="studio-detail-fda-block-head-text">
                      <div className="studio-detail-fda-block-title-row">
                        <span className="studio-detail-fda-block-title">
                          {copy.fdaBlockTitle}
                        </span>
                        {fdaEvidence.length > 0 ? (
                          <span className="studio-detail-fda-block-count">
                            {copy.fdaCountLabel(fdaEvidence.length)}
                          </span>
                        ) : null}
                      </div>
                      <p className="studio-detail-fda-block-lead">
                        {copy.fdaBlockLead}
                      </p>
                    </div>
                  </div>
                  {result.fda?.endpoint_status ? (
                    <span
                      className={`studio-detail-fda-endpoint studio-detail-fda-endpoint--${fdaEpPositive ? "on" : "off"}`}
                    >
                      {fdaEpPositive
                        ? copy.fdaEndpointOnline
                        : copy.fdaEndpointOffline}
                    </span>
                  ) : null}
                </div>
                <div className="studio-detail-fda-list">
                  {fdaEvidence.length === 0 ? (
                    <p className="studio-detail-acc-muted">
                      {lang === "zh"
                        ? "暂无 FDA 命中摘要。"
                        : "No FDA evidence snippets."}
                    </p>
                  ) : (
                    fdaEvidence.map((ev, i) => {
                      const href = fdaSourceHref(ev);
                      return (
                        <article key={i} className="studio-detail-fda-card">
                          <p className="studio-detail-fda-card-reason">
                            {ev.reason || ev.term}
                          </p>
                          <dl className="studio-detail-fda-card-meta">
                            {ev.term && ev.reason ? (
                              <div className="studio-detail-fda-meta-row">
                                <dt>{copy.fdaMetaTerm}</dt>
                                <dd>{ev.term}</dd>
                              </div>
                            ) : null}
                            {ev.product ? (
                              <div className="studio-detail-fda-meta-row">
                                <dt>{copy.fdaMetaProduct}</dt>
                                <dd>{ev.product}</dd>
                              </div>
                            ) : null}
                            {ev.classification ? (
                              <div className="studio-detail-fda-meta-row">
                                <dt>{copy.fdaMetaClass}</dt>
                                <dd>{ev.classification}</dd>
                              </div>
                            ) : null}
                            {ev.report_date ? (
                              <div className="studio-detail-fda-meta-row">
                                <dt>{copy.fdaMetaDate}</dt>
                                <dd>{ev.report_date}</dd>
                              </div>
                            ) : null}
                            {ev.source ? (
                              <div className="studio-detail-fda-meta-row">
                                <dt>{copy.fdaMetaSource}</dt>
                                <dd>{ev.source}</dd>
                              </div>
                            ) : null}
                          </dl>
                          {href ? (
                            <a
                              className="studio-detail-fda-card-link"
                              href={href}
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              <GlobalOutlined aria-hidden />
                              {copy.fdaViewSource}
                            </a>
                          ) : null}
                        </article>
                      );
                    })
                  )}
                </div>
              </section>
              <div className="studio-detail-insight-card">
                <div className="studio-detail-insight-title">
                  <ThunderboltOutlined />
                  {copy.insight}
                </div>
                <div className="studio-detail-insight-body">{insightText}</div>
                <button
                  type="button"
                  className="studio-detail-insight-btn"
                  onClick={() => void copyDisclaimerDraft()}
                >
                  {copy.applyDisclaimer}
                </button>
              </div>
            </div>
          </div>
        </div>
      </main>

      <footer className="studio-detail-footer">
        <div className="studio-detail-footer-inner">
          <div className="studio-detail-footer-left">
            <button
              type="button"
              className="studio-detail-foot-tool"
              onClick={() => onAddToCompare?.()}
            >
              <PlusSquareOutlined />
              {copy.addCompare}
            </button>
            <span className="studio-detail-foot-divider" />
            <button
              type="button"
              className="studio-detail-foot-tool"
              onClick={() => void onRerunScan()}
            >
              <FileTextOutlined />
              {copy.rescanVersion}
            </button>
            <span className="studio-detail-foot-divider" />
            <button
              type="button"
              className="studio-detail-foot-tool"
              onClick={() => setTimelineOpen(true)}
            >
              <NodeIndexOutlined />
              {copy.timeline}
            </button>
          </div>
          <div className="studio-detail-footer-right">
            <button
              type="button"
              className="studio-detail-btn-discard"
              onClick={onDiscard}
            >
              {copy.discard}
            </button>
            <button
              type="button"
              className="studio-detail-btn-finalize"
              onClick={() => handleFinalizeClick()}
            >
              <LockOutlined style={{ marginRight: 6 }} aria-hidden />
              {finalizedAt
                ? t.studioDetailFinalizeAgain
                : t.studioDetailFinalizeReport}
            </button>
          </div>
        </div>
      </footer>

      <Modal
        open={timelineOpen}
        onCancel={() => setTimelineOpen(false)}
        footer={null}
        title={copy.timeline}
        destroyOnClose
        styles={{ body: { maxHeight: "min(420px, 60vh)", overflowY: "auto" } }}
      >
        <Timeline items={timelineItems} />
      </Modal>

      <ExportPdf
        open={exportPdfOpen}
        onClose={() => setExportPdfOpen(false)}
        t={t}
        result={displayResult}
        lang={lang}
        productTitle={productTitle}
        verticalLabel={verticalLabel}
        skuLabel={skuLabel}
        scanIdShort={scanIdShort}
        scannedAt={scannedAt}
        claimsText={claimsText}
        riskLabel={riskLabel}
      />
    </div>
  );
}
